"use client";

import {
  IUserFull,
  WorkplaceRequestLocationValue,
  WorkplaceRequestTypeElement
} from "@gettactic/api";
import { useResourcesByIds, useUserList } from "@gettactic/hooks";
import { Sparkles } from "lucide-react";
import React, { useMemo } from "react";
import type { FieldValues, UseFormReturn } from "react-hook-form";
import { useWatch } from "react-hook-form";
import {
  type DeskAutomationContext,
  deskAutomationMoveNote,
  deskAutomationSentence,
  releasedDeskFateNote,
  resolveDeskAutomation
} from "../lib/desk-automation";

interface DeskAutomationPreviewProps {
  form: UseFormReturn<FieldValues>;
  type: WorkplaceRequestTypeElement;
  /** The ticket-level location picked in the wizard, if any. */
  location: WorkplaceRequestLocationValue | null;
  /** Who is filing it — what `ticket.submitter` will resolve to. */
  submitterId: string | null;
}

/**
 * Restates the type's desk automation as the submitter fills the form in, so
 * they can see what approving the request is going to do before they send it.
 *
 * Only ever speaks about approval, because that is the only thing desk rules
 * hang off — see the requires_approval guard below.
 *
 * Form inputs are keyed by field id, which is exactly what the workflow context
 * keys point at, so the live values can be resolved with no extra mapping.
 */
export const DeskAutomationPreview: React.FC<DeskAutomationPreviewProps> = ({
  form,
  type,
  location,
  submitterId
}) => {
  const usersQuery = useUserList();
  const users = (usersQuery.data?.elements ?? []) as IUserFull[];
  const values = useWatch({ control: form.control }) as FieldValues;

  const context = useMemo<DeskAutomationContext>(
    () => ({
      valueByFieldId: new Map(Object.entries(values ?? {})),
      submitterId,
      // The submitter is not the owner, and no owner exists before the request
      // is filed — a rule reading it simply has nothing to show yet.
      ownerId: null,
      locationResourceId:
        location?.type === "resource" ? location.resource_id : null
    }),
    [values, submitterId, location]
  );

  const steps = useMemo(
    () =>
      resolveDeskAutomation(type.workflows, context).filter(
        (step) => step.enabled
      ),
    [type.workflows, context]
  );

  const resourceIds = useMemo(
    () =>
      steps
        .map((step) => step.resourceId)
        .filter((id): id is string => id !== null),
    [steps]
  );
  const { resources } = useResourcesByIds(resourceIds);

  // Desk rules run off `tickets:ticket.approved`, which core only publishes
  // when someone responds to a pending approval. A type that doesn't require
  // one never has an approval to respond to, so its rules never fire and there
  // is nothing here to promise. The admin is told as much on the Linked
  // Workflows tab; the submitter can't act on it, so they just see nothing.
  if (!type.requires_approval || steps.length === 0) return null;

  const nameOf = (step: (typeof steps)[number]) => ({
    personName:
      users.find((u) => u.id === step.personId)?.name ??
      users.find((u) => u.id === step.personId)?.email ??
      null,
    deskName: resources.find((r) => r.id === step.resourceId)?.name ?? null
  });

  return (
    <div className="rounded-[10px] border border-indigo-100 bg-indigo-50/60 px-[13px] py-[11px]">
      <h4 className="flex items-center gap-1.5 font-semibold text-[12px] text-indigo-900">
        <Sparkles className="h-3.5 w-3.5 shrink-0" />
        Once approved
      </h4>
      <ul className="mt-1.5 flex flex-col gap-1">
        {steps.map((step) => {
          const moveNote = deskAutomationMoveNote(step);
          // Which desk they are leaving isn't knowable from the form alone, but
          // what becomes of it is — and losing a desk to the pool is the part a
          // submitter would want to query before, not after, approval.
          const fateNote = releasedDeskFateNote(step);
          return (
            <li key={step.workflowId} className="text-[12px] text-indigo-950">
              {deskAutomationSentence(step, nameOf(step))}
              {moveNote ? (
                <span className="text-indigo-700/80"> {moveNote}</span>
              ) : null}
              {fateNote ? (
                <span className="text-indigo-700/80"> {fateNote}</span>
              ) : null}
            </li>
          );
        })}
      </ul>
    </div>
  );
};
